import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { LicenseInfo } from '@mui/x-license';
import { customBackOfficeTheme } from './components/themes/customBackOfficeTheme';

import { MsalProvider } from "@azure/msal-react";
import {PublicClientApplication} from "@azure/msal-browser";

import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  from,
  Observable
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";

import { cache } from './config/Cache';
import {TimezoneProvider} from "./providers/TimezoneProvider";
import {timezoneStore} from "./storage/TimezoneStore";
import {CurrentUserProvider} from './providers/CurrentUserProvider';
import {UpdatedObjectCountProvider} from "./providers/UpdatedObjectCountProvider";
import {keystore} from "./storage/Keystore";
import {authenticationChannel, LOGOUT_EVENT} from './components/Root';
import reportWebVitals from './reportWebVitals';
import { authConfig, vegasTokenScopes } from "./authConfig";
import './index.css';
import './i18n';
import App from './App';
import {initSentry} from "./config/SentryConfig";

const HTTP_UNAUTHORIZED_STATUS_CODE = 401;
const APOLLO_UNAUTHENTICATED_DEFAULT_ERROR_MESSAGE = 'Unauthenticated.';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_VEGAS_GRAPHQL_ENDPOINT
});

const securityClient = new PublicClientApplication(authConfig);
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_LICENSE_KEY);

const authLink = setContext(async (_, { headers }) => {
  await securityClient.initialize();
  const account = securityClient.getAllAccounts()[0];
  if (account) {
    try {
      const response = await securityClient.acquireTokenSilent({
        account,
        ...vegasTokenScopes
      });

      return generateAuthenticationHeader(headers, response.accessToken);
    } catch (error) {
      console.error("Silent token acquisition failed in authLink: ", error);
      try {
        const response = await securityClient.acquireTokenPopup({
          account,
          ...vegasTokenScopes
        });

        return generateAuthenticationHeader(headers, response.accessToken);
      } catch (e) {
        console.error("Popup token acquisition failed: ", e);
      }
    }
  }
  return generateAuthenticationHeader(headers, null);
});

function generateAuthenticationHeader(headers, token) {
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  };
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const isUnauthenticated = networkError?.statusCode === HTTP_UNAUTHORIZED_STATUS_CODE 
      || graphQLErrors?.[0]?.message === APOLLO_UNAUTHENTICATED_DEFAULT_ERROR_MESSAGE;
  
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, auth: ${isUnauthenticated}`)
    );
  }

  if (isUnauthenticated) {
    console.warn(`[Network error]: ${networkError}, status code: ${networkError?.statusCode}`)

    const account = securityClient.getAllAccounts()[0];
    if (!account) {
      console.warn("no account found, publishing log out event...");
      authenticationChannel.postMessage({type: LOGOUT_EVENT});
      return;
    }

    console.log("Old Token expired. Trying to generate new token...");

    return new Observable(observer => {
      (async () => {
        try {
          const vegasTokenRequest = {
            account: securityClient.getAllAccounts()[0],
            ...vegasTokenScopes
          };

          console.log("Acquiring token silently");
          const tokenResult = await securityClient.acquireTokenSilent(vegasTokenRequest);

          console.log("We have a new access token, attempting to retry query....");
          const originalHeaders = operation.getContext().headers;
          
          operation.setContext(generateAuthenticationHeader(originalHeaders, tokenResult.accessToken));
          console.log("Calling forward to retry original query...");
          
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };

          forward(operation).subscribe(subscriber);

        } catch (error) {
          console.error(`error when acquiring token silently`, error);
          observer.error(error);
        }
      })();
    });
  }
});

const linkChain = from([authLink, errorLink, new RetryLink(), httpLink]);

const graphqlClient = new ApolloClient({
  link: linkChain,
  cache: cache
});

initSentry();

const root = ReactDOM.createRoot(document.getElementById('root'));

// todo: move this down into the app. Make the current app a 'Root' component
root.render(
  <React.StrictMode>
    <MsalProvider instance={securityClient}>
      <TimezoneProvider timezoneStore={timezoneStore}>
        <ApolloProvider client={graphqlClient}>
          <ThemeProvider theme={customBackOfficeTheme}>
            <CssBaseline />
            <CurrentUserProvider>
              <UpdatedObjectCountProvider keystore={keystore}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <BrowserRouter>
                    <App />
                  </BrowserRouter>
                </LocalizationProvider>
              </UpdatedObjectCountProvider>
            </CurrentUserProvider>
          </ThemeProvider>
        </ApolloProvider>
      </TimezoneProvider>
    </MsalProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
